/**
 * StoryShowcaseController.js - Controls the story showcase UI
 *
 * This module handles the story showcase modal, episode navigation,
 * and keyboard controls for the story presentation system.
 */

import { getStoryData, hasStoryData } from "./StoryData.js";

export class StoryShowcaseController {
    constructor() {
        this.currentCharacterId = null;
        this.currentEpisodeIndex = 0;
        this.isActive = false;
        this.storyData = null;

        this.initializeUI();
        this.bindEvents();
    }

    /**
     * Initialize the story showcase UI elements
     */
    initializeUI() {
        this.modal = document.getElementById("story-showcase-modal");
        this.title = document.getElementById("story-showcase-title");
        this.episodeCounter = document.getElementById("story-episode-counter");
        this.closeButton = document.getElementById("story-showcase-close");
        this.episodesContainer = document.getElementById(
            "story-episodes-container",
        );
        this.prevButton = document.getElementById("story-prev-btn");
        this.nextButton = document.getElementById("story-next-btn");
        this.progressDots = document.getElementById("story-progress-dots");

        if (!this.modal) {
            console.error("Story showcase modal not found");
            return;
        }

        console.log("Story showcase UI initialized");
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        if (!this.modal) return;

        // Close button
        if (this.closeButton) {
            this.closeButton.addEventListener("click", () =>
                this.closeStoryShowcase(),
            );
        }

        // Navigation buttons
        if (this.prevButton) {
            this.prevButton.addEventListener("click", () =>
                this.previousEpisode(),
            );
        }
        if (this.nextButton) {
            this.nextButton.addEventListener("click", () => this.nextEpisode());
        }

        // Modal backdrop click
        this.modal.addEventListener("click", (e) => {
            if (e.target === this.modal) {
                this.closeStoryShowcase();
            }
        });

        // Keyboard navigation
        document.addEventListener("keydown", (e) => this.handleKeyboard(e));

        // Window resize handler for flexible width
        window.addEventListener("resize", () => this.handleResize());
    }

    /**
     * Open the story showcase for a specific character
     * @param {string} characterId - The character ID
     */
    openStoryShowcase(characterId) {
        if (!characterId) {
            console.error("No character ID provided");
            return;
        }

        if (!hasStoryData(characterId)) {
            console.warn(`No story data found for character: ${characterId}`);
            return;
        }

        this.currentCharacterId = characterId;
        this.storyData = getStoryData(characterId);
        this.currentEpisodeIndex = 0;
        this.isActive = true;

        console.log(`Opening story showcase for ${characterId}`);

        // Scholar's Journal: record a story view (legend defaults to active).
        try { window.logJournalActivity?.('story_view', { meta: { characterId } }); } catch (e) {}

        this.updateModalContent();
        this.generateEpisodes();
        this.generateProgressDots();
        this.showEpisode(0);

        // Show modal with flexible width
        this.modal.style.display = "block";

        // Set responsive width based on screen size
        const screenWidth = window.innerWidth;
        if (screenWidth > 1400) {
            this.modal.style.width = "30%";
        } else if (screenWidth > 1200) {
            this.modal.style.width = "30%";
        } else if (screenWidth > 992) {
            this.modal.style.width = "50%";
        } else if (screenWidth > 768) {
            this.modal.style.width = "50%";
        } else if (screenWidth > 576) {
            this.modal.style.width = "80%";
        } else {
            this.modal.style.width = "100%";
        }

        this.modal.classList.add("visible");
    }

    /**
     * Close the story showcase
     */
    closeStoryShowcase() {
        if (!this.isActive) return;

        this.isActive = false;

        // Hide modal
        this.modal.classList.remove("visible");

        setTimeout(() => {
            this.modal.style.display = "none";
            this.currentCharacterId = null;
            this.storyData = null;
            this.currentEpisodeIndex = 0;
        }, 300);
    }

    /**
     * Update modal content based on current character
     */
    updateModalContent() {
        if (!this.storyData) return;

        if (this.title) {
            this.title.textContent = `${this.storyData.name} Stories`;
        }
    }

    /**
     * Generate episode HTML elements
     */
    generateEpisodes() {
        if (!this.storyData || !this.episodesContainer) return;

        this.episodesContainer.innerHTML = "";

        this.storyData.episodes.forEach((episode, index) => {
            const episodeElement = document.createElement("div");
            episodeElement.className = "story-episode";
            episodeElement.setAttribute("data-episode-index", index);

            episodeElement.innerHTML = `
                <div class="story-episode-content">
                    <div class="story-episode-title-row">
                        <h3 class="story-episode-title">${episode.title}</h3>
                        <button class="tell-me-story-btn" data-episode-index="${index}" title="Let the AI tell you this story">
                            <i class="fas fa-magic"></i>
                            <span>Tell me the story</span>
                        </button>
                    </div>
                    <p class="story-episode-description">${episode.description}</p>
                </div>
            `;

            // Add click handler for the button
            const btn = episodeElement.querySelector('.tell-me-story-btn');
            if (btn) {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(`[StoryShowcase] Tell me story button clicked for episode ${index}`);
                    this.handleTellMeStory(index);
                });
            } else {
                console.warn(`[StoryShowcase] Button not found for episode ${index}`);
            }

            this.episodesContainer.appendChild(episodeElement);
        });
    }

    /**
     * Generate progress indicator dots
     */
    generateProgressDots() {
        if (!this.storyData || !this.progressDots) return;

        this.progressDots.innerHTML = "";

        this.storyData.episodes.forEach((_, index) => {
            const dot = document.createElement("div");
            dot.className = "story-progress-dot";
            dot.setAttribute("data-episode-index", index);
            dot.addEventListener("click", () => this.showEpisode(index));

            this.progressDots.appendChild(dot);
        });
    }

    /**
     * Show a specific episode
     * @param {number} index - Episode index
     */
    showEpisode(index) {
        if (
            !this.storyData ||
            index < 0 ||
            index >= this.storyData.episodes.length
        )
            return;

        const previousIndex = this.currentEpisodeIndex;
        this.currentEpisodeIndex = index;

        // Update episode counter
        this.updateEpisodeCounter();

        // Update progress dots
        this.updateProgressDots();

        // Update navigation buttons
        this.updateNavigation();

        // Animate episode transition
        const episodes =
            this.episodesContainer.querySelectorAll(".story-episode");
        const direction = index > previousIndex ? "next" : "prev";

        episodes.forEach((episode, i) => {
            episode.classList.remove(
                "active",
                "slide-in-left",
                "slide-in-right",
                "slide-out-left",
                "slide-out-right",
            );

            if (i === index) {
                episode.classList.add("active");
                episode.classList.add(
                    direction === "next" ? "slide-in-right" : "slide-in-left",
                );
            } else if (i === previousIndex) {
                episode.classList.add(
                    direction === "next" ? "slide-out-left" : "slide-out-right",
                );
            }
        });
    }

    /**
     * Update progress dots
     */
    updateProgressDots() {
        if (!this.progressDots) return;

        const dots = this.progressDots.querySelectorAll(".story-progress-dot");
        dots.forEach((dot, index) => {
            dot.classList.toggle("active", index === this.currentEpisodeIndex);
        });
    }

    /**
     * Update episode counter
     */
    updateEpisodeCounter() {
        if (!this.episodeCounter || !this.storyData) return;

        this.episodeCounter.textContent = `Episode ${this.currentEpisodeIndex + 1} of ${this.storyData.episodes.length}`;
    }

    /**
     * Update navigation buttons
     */
    updateNavigation() {
        if (!this.storyData) return;

        if (this.prevButton) {
            this.prevButton.disabled = this.currentEpisodeIndex === 0;
        }
        if (this.nextButton) {
            this.nextButton.disabled =
                this.currentEpisodeIndex === this.storyData.episodes.length - 1;
        }
    }

    /**
     * Navigate to previous episode
     */
    previousEpisode() {
        if (this.currentEpisodeIndex > 0) {
            this.showEpisode(this.currentEpisodeIndex - 1);
        }
    }

    /**
     * Navigate to next episode
     */
    nextEpisode() {
        if (
            this.storyData &&
            this.currentEpisodeIndex < this.storyData.episodes.length - 1
        ) {
            this.showEpisode(this.currentEpisodeIndex + 1);
        }
    }

    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} e - The keyboard event
     */
    handleKeyboard(e) {
        if (!this.isActive) return;

        switch (e.key) {
            case "Escape":
                this.closeStoryShowcase();
                break;
            case "ArrowLeft":
                e.preventDefault();
                this.previousEpisode();
                break;
            case "ArrowRight":
                e.preventDefault();
                this.nextEpisode();
                break;
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
                e.preventDefault();
                const episodeIndex = parseInt(e.key) - 1;
                if (
                    this.storyData &&
                    episodeIndex >= 0 &&
                    episodeIndex < this.storyData.episodes.length
                ) {
                    this.showEpisode(episodeIndex);
                }
                break;
        }
    }

    /**
     * Handle window resize for flexible width
     */
    handleResize() {
        if (!this.isActive || !this.modal) return;

        // Adjust width based on current screen size
        const screenWidth = window.innerWidth;
        if (screenWidth > 1400) {
            this.modal.style.width = "35%";
        } else if (screenWidth > 1200) {
            this.modal.style.width = "45%";
        } else if (screenWidth > 992) {
            this.modal.style.width = "50%";
        } else if (screenWidth > 768) {
            this.modal.style.width = "60%";
        } else if (screenWidth > 576) {
            this.modal.style.width = "80%";
        } else {
            this.modal.style.width = "95%";
        }
    }

    /**
     * Check if story showcase is currently active
     * @returns {boolean} True if active
     */
    isStoryShowcaseActive() {
        return this.isActive;
    }

    /**
     * Get current episode data
     * @returns {object|null} Current episode data or null
     */
    getCurrentEpisode() {
        if (
            !this.storyData ||
            !this.storyData.episodes[this.currentEpisodeIndex]
        ) {
            return null;
        }
        return this.storyData.episodes[this.currentEpisodeIndex];
    }

    /**
     * Compile all story episodes into a single story text
     * @returns {string} Complete story text
     */
    compileFullStory() {
        if (!this.storyData || !this.storyData.episodes) {
            return "";
        }

        const characterName = this.storyData.name || "this character";
        let fullStory = `The Story of ${characterName}:\n\n`;

        this.storyData.episodes.forEach((episode, index) => {
            fullStory += `Episode ${index + 1}: ${episode.title}\n`;
            if (episode.year) {
                fullStory += `Year: ${episode.year}\n`;
            }
            fullStory += `${episode.description}\n\n`;
        });

        return fullStory.trim();
    }

    /**
     * Handle "Tell me the story" button click for a specific episode
     * Sends the episode to the chat system silently and displays AI-enhanced response
     * @param {number} episodeIndex - The index of the episode to tell
     */
    async handleTellMeStory(episodeIndex) {
        console.log(`[StoryShowcase] handleTellMeStory called for episode ${episodeIndex}`);
        
        if (!this.storyData || !this.storyData.episodes[episodeIndex]) {
            console.warn("[StoryShowcase] No story data available to tell");
            return;
        }

        const episode = this.storyData.episodes[episodeIndex];
        const characterName = this.storyData.name || "the character";
        console.log(`[StoryShowcase] Processing episode: "${episode.title}" for ${characterName}`);

        // Find the button that was clicked and set loading state
        const button = this.episodesContainer?.querySelector(`.tell-me-story-btn[data-episode-index="${episodeIndex}"]`);
        if (button) {
            button.classList.add("loading");
            const icon = button.querySelector("i");
            if (icon) {
                icon.className = "fas fa-spinner";
            }
        }

        try {
            console.log(`[StoryShowcase] Sending episode "${episode.title}" for ${characterName} to chat for AI enhancement`);

            // Create a prompt for this specific episode
            const storyPrompt = `I'd like you to tell me an enhanced, more engaging version of this story episode about ${characterName}. Here's the episode:\n\n**${episode.title}**\n\n${episode.description}\n\nPlease retell this story in your own words, making it more vivid and engaging while keeping the historical facts accurate. Focus on bringing the narrative to life.`;

            // Send to chat system silently (without showing the user's message)
            await this.sendStorySilentlyToChat(storyPrompt, characterName);

        } catch (error) {
            console.error("[StoryShowcase] Error telling story:", error);
        } finally {
            // Reset loading state
            if (button) {
                button.classList.remove("loading");
                const icon = button.querySelector("i");
                if (icon) {
                    icon.className = "fas fa-magic";
                }
            }
        }
    }

    /**
     * Send story to chat system silently without displaying user message
     * Uses the proper sendMessageSilently method which handles auth, quota, etc.
     * @param {string} storyPrompt - The prompt containing the story
     * @param {string} characterName - The name of the character
     */
    async sendStorySilentlyToChat(storyPrompt, characterName) {
        console.log(`[StoryShowcase] sendStorySilentlyToChat called for ${characterName}`);
        
        // Get reference to the chat controller if it exists
        const chatController = window.chatController;
        
        if (!chatController) {
            console.error("[StoryShowcase] Chat controller not found");
            return;
        }
        console.log(`[StoryShowcase] Chat controller found, isEnabled: ${chatController.isEnabled}`);

        // Check if chat is enabled
        if (!chatController.isEnabled) {
            console.warn("[StoryShowcase] Chat is not enabled yet");
            return;
        }

        // Close the story modal first
        console.log("[StoryShowcase] Closing story showcase modal");
        this.closeStoryShowcase();

        try {
            // Use the proper sendMessageSilently method
            // This handles auth checks, quota, interruptions, and doesn't show user message
            console.log("[StoryShowcase] Calling sendMessageSilently...");
            const response = await chatController.sendMessageSilently(storyPrompt, "story_enhancement");
            console.log("[StoryShowcase] sendMessageSilently response:", response);

            if (response && response.status === "success") {
                console.log(`[StoryShowcase] Story enhancement completed for ${characterName}`);
            } else if (response && response.status === "error") {
                console.warn(`[StoryShowcase] Story enhancement failed: ${response.error}`);
            }

        } catch (error) {
            console.error("[StoryShowcase] Failed to send story to chat:", error);
        }
    }
}
